import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  Button,
  UncontrolledTooltip,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { showModal } from '../../actions';
import {
  setCollectionsParams,
  clearCollectionsList,
  clearSelectedCollections,
  fetchCollections,
  updateCurrentCollection,
} from '../../actions/Collections';
import {
  setFilesParams,
  fetchFiles,
  clearFiles,
  refreshFiles,
} from '../../actions/Files';

import AvatarStack from '../AvatarStack';
import CollectionsBreadcrumbs from '../Collections/Breadcrumbs';
import SidebarFiltersSwitch from '../Sidebar/FiltersSwitch';
import FilesHeaderActions from './HeaderActions';

import {
  isArchive,
  isFavorites,
  isMobile,
  isSearchResults,
  isTrash,
} from '../../helpers/App';
import { userCan } from '../../helpers/Auth';
import { getParentCollection } from '../../helpers/Collections';
import {
  datePrefix,
  getFormattedDateTime,
} from '../../helpers/DateTime';
import { isAllFilesPage } from '../../helpers/Files';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { getTagTitle } from '../../helpers/Search';
import { getSetting } from '../../helpers/Settings';
import { searchTerm } from '../../helpers/Terms';

class FilesHeader extends PureComponent {
  constructor(props) {
    super(props);

    this.afterCreateCollection = this.afterCreateCollection.bind(this);
  }

  componentDidUpdate() {
    const {
      collection,
      filesParams,
      setOrder,
    } = this.props;
    const { orderby } = filesParams;

    if (collection && collection.smart && orderby === 'position') {
      setOrder('created_at', 'desc');
    }
  }

  getPageTitle() {
    const {
      intl,
      collection,
      collectionLoading,
      filesGroup,
      download,
      relatedFile,
      search,
      openCollectionEditModal,
      handleClearFiles,
      handleRefreshFiles,
      updateCurrentCollectionAction,
    } = this.props;

    if (relatedFile) {
      return intl.formatMessage({ defaultMessage: 'Files related to <em>{relatedFile}</em>' }, {
        relatedFile: relatedFile.display_name,
        em: (msg) => <em>{msg}</em>,
      });
    }

    if (search && !collection) {
      return intl.formatMessage({ defaultMessage: 'Search results' });
    }

    if (collection) {
      return (
        <>
          <span className="collection-title-content">
            {collection.name}
          </span>
          {collection.smart && (
            <>
              <Button
                className="smart-collection-badge bg-glow-violet"
                onClick={() => openCollectionEditModal({
                  className: 'modal-edit-collection',
                  collection,
                  parentCollection: getParentCollection(collection),
                  afterCreateCollection: (c) => {
                    updateCurrentCollectionAction(c);
                    handleClearFiles();
                    handleRefreshFiles();
                  },
                })}
                id="EditSmartCollectionTitleBadge"
              >
                <i className="fas fa-bolt" />
                {intl.formatMessage({
                  defaultMessage: '{count} Smart Filters',
                }, {
                  count: collection.attributes?.filters?.length,
                })}
              </Button>
              {(collection.attributes?.filters && collection.attributes?.filters.length > 0) && (
                <UncontrolledTooltip
                  fade={false}
                  target="EditSmartCollectionTitleBadge"
                  autohide={false}
                  flip={true}
                  className="smart-collection-filters-badge-tooltip"
                >
                  <div className="smart-collection-filters-badge-tooltip-inner">
                    <div className="filters-selected">
                      <div className="tags-list">
                        {collection.attributes.filters.map((sf) => {
                          const showBlocked = sf.type === 'terms' && searchTerm(sf.key)?.blocked;

                          return (
                            <div className="tag" key={`${sf.type}-${sf.key}`} title={getTagTitle(sf)}>
                              <span className="tag-label">
                                {sf.label}
                                {showBlocked && (
                                  <>
                                    <span id={`smart-collection-filters-tag-blocked-${sf.key}`}>
                                      <i className="ml-2 fa-solid fa-octagon-xmark" />
                                    </span>
                                    <UncontrolledTooltip
                                      target={`smart-collection-filters-tag-blocked-${sf.key}`}
                                    >
                                      {intl.formatMessage({
                                        defaultMessage: 'This tag is blocked',
                                      })}
                                    </UncontrolledTooltip>
                                  </>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </UncontrolledTooltip>
              )}
            </>
          )}
        </>
      );
    }

    if (collectionLoading) {
      return '';
    }

    if (filesGroup) {
      return filesGroup.name;
    }

    if (download) {
      return intl.formatMessage({ defaultMessage: 'Files downloaded' });
    }

    if (isArchive()) {
      if (getSetting('cs1')) {
        return intl.formatMessage({ defaultMessage: 'Archived Images' });
      }
      return intl.formatMessage({ defaultMessage: 'Archived Files' });
    }

    if (isFavorites()) {
      return intl.formatMessage({ defaultMessage: 'My Favorites' });
    }

    if (getSetting('cs1')) {
      return intl.formatMessage({ defaultMessage: 'Recent Images' });
    }

    if (isSearchResults()) {
      return intl.formatMessage({ defaultMessage: 'All Collections and Files' });
    }

    if (isTrash()) {
      return intl.formatMessage({ defaultMessage: 'Trash' });
    }

    return intl.formatMessage({ defaultMessage: 'All Files' });
  }

  getPageSubtitle() {
    const {
      intl,
      collection,
      filesGroup,
      download,
      profile,
    } = this.props;

    let className = '';
    let subtitle = '';

    if (collection) {
      if (collection.archived) {
        subtitle = intl.formatMessage({ defaultMessage: 'Archived Collection' });
      } else if (
        collection.ancestors &&
        collection.ancestors.length > 0 &&
        collection.ancestors[0].uuid === '-1'
      ) {
        className = 'page-subtitle';
        subtitle = intl.formatMessage({ defaultMessage: 'Your private Collections' });
      }
    }

    let groupObject = null;
    if (filesGroup) {
      groupObject = filesGroup;
    }
    if (download) {
      groupObject = download;
      groupObject.type = 'download';
    }

    if (groupObject) {
      if (
        groupObject.user &&
        groupObject.user.uuid !== profile.uuid &&
        (
          groupObject.type === 'upload' ||
          groupObject.type === 'download'
        )
      ) {
        subtitle = intl.formatMessage({ defaultMessage: 'by {name}' }, { name: groupObject.user.name });
      }
      subtitle = `${subtitle} ${datePrefix()}${getFormattedDateTime(groupObject.created_at)}`;
    }

    if (subtitle) {
      return (
        <span className={className}>{subtitle}</span>
      );
    }

    return null;
  }

  getPageSecondSubtitle() {
    const {
      collection,
    } = this.props;

    if (!collection || !collection.description) return null;

    return (
      <span className="page-second-subtitle">
        {collection.description}
      </span>
    );
  }

  afterCreateCollection(newCollection) {
    const {
      intl,
      reloadCollection,
      collection,
    } = this.props;

    addFlashMessage({
      type: 'success',
      text: intl.formatMessage({ defaultMessage: 'A new Collection has been created' }),
      button: {
        url: `/collections/${newCollection.uuid}`,
        text: intl.formatMessage({ defaultMessage: 'Open Collection' }),
      },
    });

    const parentCollection = getParentCollection(newCollection);
    if (parentCollection && collection.uuid === parentCollection.uuid) {
      reloadCollection();
    }
  }

  render() {
    const {
      collection,
      handleChangeAccess,
    } = this.props;
    const mobile = isMobile();
    const headerActions = (collection || isAllFilesPage() || isTrash()) ? (
      <FilesHeaderActions afterCreateCollection={this.afterCreateCollection} />
    ) : null;

    let allowed = [];
    if (collection) {
      const allowedGroups = collection.allowed_groups || [];
      const allowedUsers = collection.allowed_users || [];
      allowed = allowedGroups.concat(allowedUsers).map((item) => ({
        uuid: item.uuid,
        type: item.email ? 'user' : 'group',
        name: item.name ? item.name : '',
        email: item.email ? item.email : undefined,
        avatar_url: item.avatar_url ? item.avatar_url : null,
      }));
    }

    return (
      <header
        className={classnames('page-header', {
          'with-upgrade-button': isArchive(),
          'page-header-collection': collection,
        })}
      >
        <div className="filters-switch-wrapper">
          <SidebarFiltersSwitch />
          {mobile && headerActions}
        </div>
        <div className={classnames('page-header-left', { 'page-header-left-collection': collection })}>
          <div>
            {collection && (
              <CollectionsBreadcrumbs collection={collection} />
            )}
            <div className="page-header-title mb-0">
              <h2
                className={classnames('page-title', 'collection-title', {
                  archived: collection && collection.archived,
                  'mb-2': collection && collection.description,
                })}
              >
                {this.getPageTitle()}
                {this.getPageSubtitle()}
              </h2>
              {(collection && !collection.public && !getSetting('cs1')) && (
                <AvatarStack
                  size={30}
                  users={allowed}
                  onClick={() => {
                    if (userCan('collection.change_access', collection)) {
                      handleChangeAccess(collection.uuid);
                    }
                  }}
                />
              )}
            </div>
            <div className="page-header-subtitle">
              <div>{this.getPageSecondSubtitle()}</div>
            </div>
          </div>
        </div>
        {!mobile && headerActions}
      </header>
    );
  }
}

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  files: state.Files.files,
  filesTotal: state.Files.filesTotal,
  filesParams: state.Files.params,
  search: state.Search.search,
  selectedFiles: state.Files.selectedFiles,
  collection: state.Collections.collection,
  collectionLoading: state.Collections.collectionLoading,
  collections: state.Collections.collections,
  selectedCollections: state.Collections.selectedCollections,
  filesGroup: state.Files.filesGroup,
  download: state.Files.download,
  reorderingMode: state.Files.reorderingMode,
});

const mapDispatchToProps = (dispatch) => ({
  setOrder: (orderby, order) => {
    dispatch(clearFiles());
    dispatch(setFilesParams({
      orderby,
      order,
      page: 0,
    }));
    dispatch(fetchFiles());
    dispatch(setCollectionsParams({ page: 0 }));
    dispatch(clearCollectionsList());
    dispatch(clearSelectedCollections());
    dispatch(fetchCollections());
  },
  openCollectionEditModal: (params) => {
    dispatch(showModal('CollectionEditModal', params));
  },
  updateCurrentCollectionAction: collection => (
    dispatch(updateCurrentCollection(collection))
  ),
  reloadCollection: () => {
    dispatch(setCollectionsParams({ page: 0 }));
    dispatch(clearCollectionsList());
    dispatch(clearSelectedCollections());
    dispatch(fetchCollections());
  },
  handleRefreshFiles: () => {
    dispatch(clearFiles());
    dispatch(refreshFiles());
  },
  handleClearFiles: () => {
    dispatch(clearFiles());
  },
  handleChangeAccess: (collectionUuid) => {
    dispatch(showModal('AccessModal', {
      className: 'modal-access modal-file-access',
      collections: [collectionUuid],
    }));
  },
});

FilesHeader.propTypes = {
  intl: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  filesParams: PropTypes.object.isRequired,
  search: PropTypes.string,
  collection: PropTypes.object,
  collectionLoading: PropTypes.bool.isRequired,
  filesGroup: PropTypes.object,
  download: PropTypes.object,
  relatedFile: PropTypes.object,
  setOrder: PropTypes.func.isRequired,
  openCollectionEditModal: PropTypes.func.isRequired,
  updateCurrentCollectionAction: PropTypes.func.isRequired,
  reloadCollection: PropTypes.func.isRequired,
  handleRefreshFiles: PropTypes.func.isRequired,
  handleClearFiles: PropTypes.func.isRequired,
  handleChangeAccess: PropTypes.func.isRequired,
};

FilesHeader.defaultProps = {
  collection: null,
  filesGroup: null,
  download: null,
  relatedFile: null,
  search: null,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(FilesHeader));
